import { toast } from "@inno-ui/toast";
import { parse } from "date-fns";
import { format } from "date-fns/format";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { useAppointmentStore } from "../../store/appointment/appointment-store";
import { DATE_FORMAT } from "./calendar.constants";
import { CalendarViews } from "./calendar.enums";
import { MonthSelector } from "./month-selector";
import styles from "./styles/calendar.module.scss";
import { Weeks } from "./weeks";

type Props = {
  onSelectDate?: () => void;
};

export function Calendar({ onSelectDate }: Props) {
  const today = new Date();
  const [params] = useSearchParams();
  const urlDate = params.get("date");
  const [currentView, setCurrentView] = useState<CalendarViews>(
    CalendarViews.Weeks,
  );
  const [year, setYear] = useState<number>(today.getFullYear());
  const [month, setMonth] = useState<number>(today.getMonth());
  const [dateToDisplay, setDateToDisplay] = useState<Date>(today);
  const [isUrlDateProcessing, setIsUrlDateProcessing] = useState(false);
  const { setDate, setGlobalDateToDisplay, resetTimeRange } = useAppointmentStore();

  const updateDateToDisplay = useCallback(() => {
    if (isUrlDateProcessing) {
      return;
    }
    const newDate = new Date(year, month, 1);
    setDateToDisplay(newDate);
  }, [month, year, isUrlDateProcessing]);

  useEffect(() => {
    updateDateToDisplay();
  }, [updateDateToDisplay]);

  // Initialize calendar/date from URL if provided
  useEffect(() => {
    if (!urlDate)
      return;

    const parsed = parse(urlDate, DATE_FORMAT, new Date());
    if (Number.isNaN(parsed.getTime()))
      return;

    // compare date-only (ignore time)
    const normalizedToday = new Date();
    normalizedToday.setHours(0, 0, 0, 0);
    const normalizedParsed = new Date(parsed);
    normalizedParsed.setHours(0, 0, 0, 0);

    const target = normalizedParsed < normalizedToday ? normalizedToday : normalizedParsed;

    if (normalizedParsed < normalizedToday) {
      toast.error({ title: "Cannot select a past date" });
    }

    // Set flag to prevent updateDateToDisplay from overriding
    setIsUrlDateProcessing(true);

    // Update local calendar view to month/year of target first
    setYear(target.getFullYear());
    setMonth(target.getMonth());

    // Set the dateToDisplay to the actual target date, not the 1st of the month
    setDateToDisplay(target);

    // Then update global appointment state on next tick to avoid race with Weeks effect
    setTimeout(() => {
      resetTimeRange();
      setGlobalDateToDisplay(target);
      setDate(format(target, DATE_FORMAT));
      // Reset the flag after processing
      setIsUrlDateProcessing(false);
    }, 0);
  }, [urlDate, setDate, setGlobalDateToDisplay, resetTimeRange]);

  const renderComponent = () => {
    switch (currentView) {
      case CalendarViews.MonthSelector:
        return (
          <MonthSelector
            setCurrentView={setCurrentView}
            year={year}
            setYear={setYear}
            setMonth={setMonth}
          />
        );
      case CalendarViews.Weeks:
      default:
        return (
          <Weeks
            setCurrentView={setCurrentView}
            dateToDisplay={dateToDisplay}
            setDateToDisplay={setDateToDisplay}
            onSelectDate={onSelectDate}
          />
        );
    }
  };
  return <div className={styles.calendar}>{renderComponent()}</div>;
}
